import { useState } from 'react';
import { authApi } from '../services/api';
import { FileCheck, Shield, Zap, ArrowRight, Eye } from 'lucide-react';
import cloudbyzLogo from '../assets/cloudbyz-logo.png';

function ClosedEye({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ transform: 'scaleY(-1)' }}>
      <path d="M2 12c2.5-4 5.5-6 10-6s7.5 2 10 6" />
      <line x1="12" y1="6" x2="12" y2="4" />
      <line x1="7" y1="7" x2="5.5" y2="5.5" />
      <line x1="17" y1="7" x2="18.5" y2="5.5" />
    </svg>
  );
}

interface SetupPageProps {
  onSetupComplete: () => void;
}

const FEATURES = [
  { icon: FileCheck, text: 'Automated ANDA/NDA checklist generation against FDA requirements' },
  { icon: Shield,    text: 'AI-powered document validation with evidence tracing' },
  { icon: Zap,       text: 'Instant compliance gap analysis and remediation guidance' },
];

const PASSWORD_HINT = 'Min 12 characters · 1 uppercase · 1 number · 1 special character';

export function SetupPage({ onSetupComplete }: SetupPageProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.runSetup(name.trim(), email.trim(), password);
      setDone(true);
      setTimeout(onSetupComplete, 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0F172A 0%, #0D2E5E 60%, #0B4DA6 100%)' }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10"
               style={{ background: 'radial-gradient(circle, #007BFF, transparent)' }} />
          <div className="absolute bottom-0 -left-16 w-72 h-72 rounded-full opacity-10"
               style={{ background: 'radial-gradient(circle, #3B82F6, transparent)' }} />
          <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center ring-1 ring-white/20">
            <FileCheck className="w-6 h-6 text-blue-300" />
          </div>
          <span className="text-xl font-bold tracking-tight">AI RegSubmission Agent</span>
        </div>
        <div className="relative space-y-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6"
                 style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              First-time Setup
            </div>
            <h1 className="text-4xl font-bold leading-tight mb-4">
              Welcome to<br />
              <span style={{ color: '#60A5FA' }}>AI RegSubmission Agent</span>
            </h1>
            <p className="text-base" style={{ color: 'rgba(148,163,184,0.9)' }}>
              Create your admin account to get started. You can add team members after setup.
            </p>
          </div>
          <div className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center"
                     style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.25)' }}>
                  <Icon className="w-4 h-4" style={{ color: '#93C5FD' }} />
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(203,213,225,0.9)' }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md flex flex-col items-center mb-6">
          <img src={cloudbyzLogo} alt="Cloudbyz" className="h-10 object-contain" />
        </div>

        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-foreground">Create Admin Account</h2>
              <p className="text-sm text-muted-foreground mt-1">
                This is a one-time setup. After this, only admins can create new users.
              </p>
            </div>

            {done ? (
              <div className="flex items-center gap-3 px-4 py-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm text-green-700 font-medium">Admin account created! Redirecting to login…</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Jane Smith"
                    required
                    className="w-full px-4 py-2.5 border border-border rounded-xl text-sm bg-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Work Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="admin@company.com"
                    required
                    className="w-full px-4 py-2.5 border border-border rounded-xl text-sm bg-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      maxLength={128}
                      className="w-full px-4 py-2.5 border border-border rounded-xl text-sm bg-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                      tabIndex={-1}
                    >
                      {showPassword ? <Eye className="w-4 h-4" /> : <ClosedEye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1.5">{PASSWORD_HINT}</p>
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                    <div className="w-4 h-4 rounded-full bg-red-500 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm mt-2"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating account…</>
                  ) : (
                    <>Create Admin Account<ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
